/**
 * Repository for fetching time tracking history data.
 *
 * Reads from: `clients/{clientId}/users/{userId}/timeTracking/{monthDocId}`
 *
 * Builds a Tagesverlauf (day timeline) matching IRES Work's Day History view:
 * - Start/End of Day markers
 * - Project blocks with sub-entries (work, travel, pause)
 * - Top-level pauses, travel, interruptions
 * - Computed "General" gap entries (>5 min threshold)
 * - Consecutive interruptions merged (5 min threshold). Project entries are NOT
 *   merged: each renders as its own row (diverges from IRES Work by design).
 * - Duration calculated from timestamps, formatted as Xm / Xh YYm
 */

import { db } from "@/main";
import { functions } from "@/main";
import { getClientRef } from "./ClientRepository";
import { getActiveUserId } from "./UserRepository";
import {
  AutoTimeTrackingContext,
  TimeTrackingMonth,
  TimeTrackingDayEntry,
  TimeTrackingDisplayEntry,
  TimeTrackingDaySummary,
  TimeTrackingProjectEntry,
  TimeTrackingProjectTask,
  TimeTrackingEndReason,
  TimeTrackingEntryState,
  TimeTrackingOtherEntry,
  TimelineSubEntry,
  SuspendEntry,
  SuspendType,
  ProjectTimeTrackingLog,
  TIMELINE_COLORS,
  PreviewOpenTimeTrackingAutoCloseRequest,
  PreviewOpenTimeTrackingAutoCloseResponse,
  ApplyOpenTimeTrackingAutoCloseRequest,
  ApplyOpenTimeTrackingAutoCloseResponse,
} from "@/data/models/TimeTracking";
import { ProjectOperationSource } from "@/data/models/ProjectDetails";
import { getArchivedProjectRef, getProjectRef } from "./ProjectPaths";

/**
 * Builds the Firestore path to the timeTracking subcollection.
 * @param userId - If provided, targets that user's data; otherwise uses the logged-in user.
 */
function getTimeTrackingRef(userId?: string) {
  const uid = userId ?? getActiveUserId();
  return db.collection(`${getClientRef()}/users/${uid}/timeTracking`);
}

/**
 * Builds the month document ID for a given date.
 * Format: `YYYY-M{zeroIndexedMonth}` (e.g. May 2026 → "2026-M4").
 */
export function getMonthDocId(date: Date): string {
  return `${date.getFullYear()}-M${date.getMonth()}`;
}

/**
 * Builds the day map key for a given date.
 * Format: `MM-DD` (e.g. May 21 → "05-21").
 */
export function getDayKey(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${month}-${day}`;
}

/**
 * Fetches the time tracking month document for the given or logged-in user.
 * Returns null if the document does not exist.
 */
export async function getTimeTrackingMonth(date: Date, userId?: string): Promise<TimeTrackingMonth | null> {
  const docId = getMonthDocId(date);
  const doc = await getTimeTrackingRef(userId).doc(docId).get();

  if (!doc.exists) {
    return null;
  }

  return doc.data() as TimeTrackingMonth;
}

/**
 * Subscribes to realtime updates on the time tracking month document.
 * Calls `onData` with the latest month data whenever it changes.
 * Returns an unsubscribe function to stop listening.
 */
export function onTimeTrackingMonthSnapshot(
  date: Date,
  onData: (month: TimeTrackingMonth | null) => void,
  userId?: string
): () => void {
  const docId = getMonthDocId(date);
  return getTimeTrackingRef(userId).doc(docId).onSnapshot((doc) => {
    if (!doc.exists) {
      onData(null);
      return;
    }
    onData(doc.data() as TimeTrackingMonth);
  });
}

/**
 * Extracts the day entry for a specific date from a month document.
 * Returns null if no entries exist for that day.
 */
export function getDayEntry(
  month: TimeTrackingMonth,
  date: Date
): TimeTrackingDayEntry | null {
  const key = getDayKey(date);
  return month.days?.[key] ?? null;
}

/** Doc ref for a shared (team) time-tracking entry within the active client. */
function getSharedTimeTrackingEntryRef(sharedEntryId: string) {
  return db.doc(`${getClientRef()}/sharedTimeTrackingEntries/${sharedEntryId}`);
}

/**
 * Resolves a day's `sharedEntries` references to their authoritative shared docs
 * (project-entry shaped), keyed by shared-entry id. Missing/unreadable refs are skipped.
 * Each resolved entry is tagged `isSharedEntry: true` so the UI can flag team entries.
 */
export async function resolveSharedEntriesForDay(
  day: TimeTrackingDayEntry
): Promise<Record<string, TimeTrackingProjectEntry>> {
  const references = day.sharedEntries ?? {};
  const ids = [
    ...new Set(
      Object.values(references)
        .map((ref) => ref?.sharedEntryId)
        .filter((id): id is string => !!id)
    ),
  ];

  if (ids.length === 0) {
    return {};
  }

  const snapshots = await Promise.all(
    ids.map((id) => getSharedTimeTrackingEntryRef(id).get())
  );

  const result: Record<string, TimeTrackingProjectEntry> = {};
  snapshots.forEach((snapshot, index) => {
    if (snapshot.exists) {
      result[ids[index]] = {
        ...(snapshot.data() as TimeTrackingProjectEntry),
        isSharedEntry: true,
      };
    }
  });

  return result;
}

/**
 * Returns a shallow copy of the day with resolved shared entries merged into
 * `projectEntries`, so the existing timeline/day-summary logic renders them uniformly.
 * A shared-task member holds only a reference (no private projectEntry), so ids don't
 * collide; if one ever did, the user's own entry wins (spread last).
 */
export function mergeSharedProjectEntries(
  day: TimeTrackingDayEntry,
  sharedById: Record<string, TimeTrackingProjectEntry>
): TimeTrackingDayEntry {
  if (Object.keys(sharedById).length === 0) {
    return day;
  }

  return {
    ...day,
    projectEntries: { ...sharedById, ...(day.projectEntries ?? {}) },
  };
}

// ── Task & phase label mapping ──

/** Maps a ProjectTasks enum value to a translation key. */
export function getTaskLabel(task: string | undefined | null): string | null {
  if (!task) return null;
  switch (task) {
    case TimeTrackingProjectTask.ORDER_ENTRY: return "timeTrackingHistory.tasks.orderEntry";
    case TimeTrackingProjectTask.FIRST_CONTACT: return "timeTrackingHistory.tasks.firstContact";
    case TimeTrackingProjectTask.LEAK_DETECTION: return "timeTrackingHistory.tasks.leakDetection";
    case TimeTrackingProjectTask.DAMAGE_ASSESSMENT: return "timeTrackingHistory.tasks.damageAssessment";
    case TimeTrackingProjectTask.INSURANCE_OFFER: return "timeTrackingHistory.tasks.insuranceOffer";
    case TimeTrackingProjectTask.DRYING_APPROVAL: return "timeTrackingHistory.tasks.dryingApproval";
    case TimeTrackingProjectTask.DRYING_PREPARATION: return "timeTrackingHistory.tasks.dryingPreparation";
    case TimeTrackingProjectTask.INTERMEDIATE_MEASUREMENT: return "timeTrackingHistory.tasks.intermediateMeasurement";
    case TimeTrackingProjectTask.DRYING_CONCLUSION: return "timeTrackingHistory.tasks.dryingConclusion";
    case TimeTrackingProjectTask.REPAIR: return "timeTrackingHistory.tasks.repair";
    case TimeTrackingProjectTask.PROJECT_CONCLUSION: return "timeTrackingHistory.tasks.projectConclusion";
    default: return null;
  }
}

/**
 * Maps AutoTimeTrackingContext enum values (or task values carrying the same values)
 * to the phase column translation key used in Time Tracking History.
 */
export function getAutoTimeTrackingContextLabel(
  context: string | undefined | null
): string | null {
  if (!context) return null;
  switch (context) {
    case AutoTimeTrackingContext.LEAK_DETECTION:
      return "timeTrackingHistory.tasks.leakDetection";
    case AutoTimeTrackingContext.DAMAGE_ASSESSMENT:
      return "timeTrackingHistory.tasks.damageAssessment";
    case AutoTimeTrackingContext.PREPARE_DRYING:
      return "timeTrackingHistory.tasks.dryingPreparation";
    case AutoTimeTrackingContext.FINAL_MEASUREMENT:
      return "timeTrackingHistory.tasks.intermediateMeasurement";
    case AutoTimeTrackingContext.FINAL_DRYING:
      return "timeTrackingHistory.tasks.dryingConclusion";
    case AutoTimeTrackingContext.REPAIR:
      return "timeTrackingHistory.tasks.repair";
    default:
      return null;
  }
}

/**
 * Maps a project phase to a display label (fallback when task is not set).
 */
export function getPhaseLabel(phase: string): string {
  switch (phase) {
    case "SET_UP": return "timeTrackingHistory.phases.damageAssessment";
    case "LEAK_DETECTION": return "timeTrackingHistory.phases.leakDetection";
    case "DAMAGE_ASSESSMENT": return "timeTrackingHistory.phases.damageAssessment";
    case "DAMAGE_ASSESSMENT_WITH_FIRST_MEASURES": return "timeTrackingHistory.phases.damageAssessment";
    case "DRYING": return "timeTrackingHistory.phases.dryingPreparation";
    case "REPAIR": return "timeTrackingHistory.phases.repair";
    default: return phase;
  }
}

/**
 * i18n key of the task shown for a project log entry: pause and travel segments first (they carry
 * no task of their own), then the auto time tracking context, then the project phase as fallback.
 */
export function getProjectLogTaskLabel(log: ProjectTimeTrackingLog): string {
  if (log.countsAsPause) return "timeTrackingHistory.categories.pause";
  if (log.countsAsTravel) return "timeTrackingHistory.travel.approach";

  return (
    getAutoTimeTrackingContextLabel(log.autoTimeTrackingContext) ??
    getPhaseLabel(log.projectPhase)
  );
}

/** Resolves the label for a project work sub-entry: task → stepAtStart.task → stepAtEnd.task → phase. */
function resolveWorkLabel(
  item: { name?: string; stepAtStart?: { task?: string } | null; stepAtEnd?: { task?: string } | null },
  task: string | undefined | null,
  projectPhase: string,
  t: (key: string) => string
): string {
  const taskKey = getTaskLabel(task)
    ?? getTaskLabel(item.stepAtStart?.task)
    ?? getTaskLabel(item.stepAtEnd?.task);
  if (taskKey) return t(taskKey);
  return t(getPhaseLabel(projectPhase));
}

/** Maps a travel destination (location field) to a label. */
function getTravelLabel(location: string | null | undefined, isProjectTravel: boolean, t: (key: string) => string): string {
  switch (location?.toUpperCase()) {
    case "OFFICE": return t("timeTrackingHistory.travel.toOffice");
    case "HOME": return t("timeTrackingHistory.travel.toHome");
    case "PROJECT": return isProjectTravel
      ? t("timeTrackingHistory.travel.approach")
      : t("timeTrackingHistory.travel.toProject");
    default: return isProjectTravel
      ? t("timeTrackingHistory.travel.approach")
      : t("timeTrackingHistory.travel.toProject");
  }
}

// ── Duration formatting (Tagesverlauf: Xm / Xh YYm) ──

/**
 * Calculates duration in whole minutes from two dates.
 * Seconds and milliseconds are ignored for local calculations.
 * Returns 0 if either date is null.
 */
export function calcDurationMinutes(start: Date | null, end: Date | null): number {
  if (!start || !end) return 0;
  const startMinute = new Date(start);
  startMinute.setSeconds(0, 0);
  const endMinute = new Date(end);
  endMinute.setSeconds(0, 0);

  const diffMs = endMinute.getTime() - startMinute.getTime();
  return Math.floor(diffMs / (1000 * 60));
}

/**
 * Formats minutes as "Xm" or "Xh YYm" matching IRES Work's formatHoursAndMinutes().
 */
export function formatDuration(minutes: number): string {
  if (minutes === 0) return "0m";
  if (minutes < 0) return `-${formatDuration(-minutes)}`;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}m`;
  return `${m}m`;
}

/**
 * Legacy format: minutes to hours string matching IRES Project DecimalFormat("####.##").
 * Used for day summary only.
 */
export function formatMinutesToHours(minutes: number, hideZero = true, locale?: string): string {
  if (hideZero && minutes === 0) return "";
  if (minutes === 0) return "0 h";
  const hours = minutes / 60;
  const formatter = new Intl.NumberFormat(locale, {
    maximumFractionDigits: 2,
    useGrouping: false,
  });
  const formatted = formatter.format(hours);
  return `${formatted} h`;
}

// ── Start/end time resolution ──

function getActualStartOfWork(day: TimeTrackingDayEntry): Date | null {
  const userSet = day.userSetStartOfWork;
  if (userSet) return userSet.toDate();
  return day.startOfWork ? day.startOfWork.toDate() : null;
}

function getActualEndOfWork(day: TimeTrackingDayEntry): Date | null {
  const userSet = day.userSetEndOfWork;
  const endOfWork = day.endOfWork;

  if (!userSet && endOfWork) return endOfWork.toDate();
  if (userSet && !endOfWork) return null;
  if (userSet && endOfWork && day.reopenedBy) return endOfWork.toDate();
  if (userSet) return userSet.toDate();
  return null;
}

function getProjectActualStart(pe: TimeTrackingProjectEntry): Date | null {
  if (pe.userSetStartOfWork) return pe.userSetStartOfWork.toDate();
  if (pe.workStartTime) return pe.workStartTime.toDate();
  if (pe.createdBy?.recordedAt) return pe.createdBy.recordedAt.toDate();
  return null;
}

function getProjectActualEnd(pe: TimeTrackingProjectEntry): Date | null {
  if (pe.userSetEndOfWork) return pe.userSetEndOfWork.toDate();
  if (pe.workEndTime) return pe.workEndTime.toDate();
  return null;
}

function getOtherActualStart(oe: TimeTrackingOtherEntry): Date | null {
  if (oe.userSetStartOfWork) return oe.userSetStartOfWork.toDate();
  if (oe.workStartDate) return oe.workStartDate.toDate();
  if (oe.startDate) return oe.startDate.toDate();
  if (oe.createdBy?.recordedAt) return oe.createdBy.recordedAt.toDate();
  return null;
}

function getOtherActualEnd(oe: TimeTrackingOtherEntry): Date | null {
  if (oe.userSetEndOfWork) return oe.userSetEndOfWork.toDate();
  if (oe.workEndDate) return oe.workEndDate.toDate();
  return null;
}

function getDayBounds(day: TimeTrackingDayEntry): { dayStart: Date; dayEnd: Date } {
  const base = day.date.toDate();
  const dayStart = new Date(base);
  dayStart.setHours(0, 0, 0, 0);
  const dayEnd = new Date(base);
  dayEnd.setHours(23, 59, 59, 999);
  return { dayStart, dayEnd };
}

function clampRangeToDay(
  start: Date,
  end: Date,
  dayStart: Date,
  dayEnd: Date
): { start: Date; end: Date; startWasClamped: boolean; endWasClamped: boolean } | null {
  const clampedStartMs = Math.max(start.getTime(), dayStart.getTime());
  const clampedEndMs = Math.min(end.getTime(), dayEnd.getTime());
  // Drop only ranges that fall entirely outside the day (clamped end before start).
  // A zero-duration in-range entry (clamped start === end) is KEPT as a point boundary so
  // it still advances the gap walker's `lastEnd` — otherwise a 0-minute entry (e.g. a stale
  // task auto-closed at its start time) would be skipped and a general gap would span right
  // over it to day-end.
  if (clampedEndMs < clampedStartMs) return null;
  return {
    start: new Date(clampedStartMs),
    end: new Date(clampedEndMs),
    startWasClamped: clampedStartMs !== start.getTime(),
    endWasClamped: clampedEndMs !== end.getTime(),
  };
}

// ── Sub-entry builders ──

/** FQN type discriminator prefixes for polymorphic data[] items. */
const TYPE_TRAVEL = "ProjectTravelTrackingEntryEntity";
const TYPE_PAUSE = "ProjectPauseTrackingEntryEntity";
const TYPE_SUSPEND = "ProjectSuspendTrackingEntryEntity";

function isSubEntryType(typeStr: string | undefined, target: string): boolean {
  if (!typeStr) return false;
  return typeStr.includes(target);
}

function buildSubEntries(
  pe: TimeTrackingProjectEntry,
  t: (key: string) => string
): TimelineSubEntry[] {
  const subs: TimelineSubEntry[] = [];
  for (const item of (pe.data ?? [])) {
    let type: TimelineSubEntry["type"] = "work";
    let label = "";
    let task: string | null = null;

    if (isSubEntryType(item.type, TYPE_SUSPEND) || isSubEntryType(item.type, TYPE_PAUSE)) {
      // Suspend markers act as pause boundaries — show as pause rows to prevent
      // merging of consecutive same-task work entries (matches IRES Work behavior).
      type = "pause";
      label = t("timeTrackingHistory.categories.pause");
    } else if (isSubEntryType(item.type, TYPE_TRAVEL)) {
      type = "travel";
      label = getTravelLabel(item.endLocation, true, t);
    } else {
      // Work entry — resolve label from task chain
      type = "work";
      task = item.stepAtStart?.task ?? item.stepAtEnd?.task ?? pe.task ?? null;
      label = resolveWorkLabel(item, pe.task, pe.projectPhase, t);
    }

    subs.push({
      id: `${pe.id}_${item.name}_${item.startTime?.seconds ?? subs.length}`,
      type,
      countsAsTravel: type === "travel",
      task,
      startTime: item.startTime ? item.startTime.toDate() : null,
      endTime: item.endTime ? item.endTime.toDate() : null,
      label,
      wasStartEdited: item.wasStartEdited ?? false,
      wasEndEdited: item.wasEndEdited ?? false,
      // Sub-entries inherit source from the parent project entry
      startSource: pe.startSource ?? null,
      endSource: pe.endSource ?? null,
    });
  }

  // Newer format: data[] may be empty but entry-level timing exists.
  // Synthesize a single work/travel/pause sub-entry from the project entry itself.
  if (
    subs.length === 0 &&
    pe.workStartTime &&
    (pe.task || pe.countsAsTravel === true || pe.countsAsPause === true)
  ) {
    const taskLabel = getTaskLabel(pe.task);
    const isTravel = pe.countsAsTravel === true;
    const isPause = pe.countsAsPause === true;
    subs.push({
      id: `${pe.id}_task`,
      type: isPause ? "pause" : (isTravel ? "travel" : "work"),
      countsAsTravel: isTravel,
      task: isTravel || isPause ? null : pe.task,
      // Prioritize user-edited times over the original recorded times, matching the
      // parent project block (getProjectActualStart/End). Without this the sub-entry
      // row shows the original time while flagged as edited (red).
      startTime: getProjectActualStart(pe),
      endTime: getProjectActualEnd(pe),
      label: isPause
        ? t("timeTrackingHistory.categories.pause")
        : isTravel
        ? getTravelLabel("PROJECT", true, t)
        : (taskLabel ? t(taskLabel) : t(getPhaseLabel(pe.projectPhase))),
      wasStartEdited: pe.userSetStartOfWork != null,
      wasEndEdited: pe.userSetEndOfWork != null,
      startSource: pe.startSource ?? null,
      endSource: pe.endSource ?? null,
    });
  }

  // Sort sub-entries by start time
  subs.sort((a, b) => (a.startTime?.getTime() ?? 0) - (b.startTime?.getTime() ?? 0));
  return subs;
}

// ── Project name helper ──

function truncateProjectName(name?: string | null): string {
  if (!name) return "";
  return name.length > 20 ? name.substring(0, 20) : name;
}

// ── Main timeline builder ──

/** Intermediate project entry, one per Firestore project entry (no merging). */
interface RawProjectBlock {
  entryId: string;
  projectId: string;
  projectName: string;
  phaseLabel: string;
  startTime: Date;
  endTime: Date | null;
  subEntries: TimelineSubEntry[];
  wasStartEdited: boolean;
  wasEndEdited: boolean;
  /** Entry-level task, used for sub-entry labelling and pause splitting. */
  task: string | null;
  countsAsTravel?: boolean;
  countsAsPause?: boolean;
  /** Raw suspend entries from Firestore (carried through; used by silent-suspend step). */
  suspendEntries?: SuspendEntry[];
  startSource?: ProjectOperationSource | null;
  endSource?: ProjectOperationSource | null;
  /** Persisted close reason (suspend vs finish); drives the derived display state. */
  endReason?: TimeTrackingEndReason | string | null;
}

// ── Overlap (illegal-boundary) detection ──

/** A time span used for overlap detection. `endMs = null` means still running. */
export interface OverlapItem {
  id: string;
  startMs: number;
  endMs: number | null;
}

/** Per-item overlap flags: which boundary of the item is involved in an overlap. */
export interface OverlapMarkers {
  start: boolean;
  end: boolean;
}

/**
 * Detects illegal overlaps between time spans and returns per-item boundary flags
 * used to render the `*` marker.
 *
 * Comparison is at **minute precision** (seconds/milliseconds ignored): two spans that
 * merely touch within the same minute (e.g. `13:43–13:45` then `13:45–…`) are NOT an
 * overlap. An overlap exists only when a later span's start minute is strictly before an
 * earlier span's end minute — then the earlier span's `end` and the later span's `start`
 * are flagged. Running items (`endMs = null`) use `nowMs` as their effective end.
 *
 * The caller decides which items to include (e.g. Row 2 excludes pause/travel logs).
 */
export function computeOverlapMarkers(
  items: OverlapItem[],
  nowMs: number
): Record<string, OverlapMarkers> {
  const toMinute = (ms: number) => Math.floor(ms / 60000);
  const sorted = [...items].sort((a, b) => a.startMs - b.startMs);

  const markers: Record<string, OverlapMarkers> = {};
  for (const item of sorted) markers[item.id] = { start: false, end: false };

  for (let i = 0; i < sorted.length; i++) {
    for (let j = i + 1; j < sorted.length; j++) {
      const earlier = sorted[i];
      const later = sorted[j];
      const earlierEnd = earlier.endMs ?? nowMs;
      // Sorted by start => once a later start is not before this end, no further
      // later item can overlap it either.
      if (toMinute(later.startMs) < toMinute(earlierEnd)) {
        markers[earlier.id].end = true;
        markers[later.id].start = true;
      } else {
        break;
      }
    }
  }

  return markers;
}

/**
 * Derives the lifecycle state of a task time-tracking entry for display.
 *
 * The rule is intentionally driven by the REAL persisted end + the persisted close
 * reason, NOT by any synthetic display end (e.g. a silent-interruption end): an entry
 * with no real end is RUNNING even if it was silently interrupted (app backgrounded,
 * never resumed) — that is not a deliberate finish.
 *
 * - No end (`actualEnd == null`) ⇒ RUNNING.
 * - Ended with `endReason === SUSPENDED` ⇒ SUSPENDED.
 * - Ended otherwise (COMPLETED, or absent on legacy entries) ⇒ FINISHED.
 *
 * @param actualEnd - The entry's real persisted end (workEndTime/userSetEndOfWork), or null.
 * @param endReason - The persisted close reason, if any.
 */
export function deriveEntryState(
  actualEnd: Date | null,
  endReason: TimeTrackingEndReason | string | null | undefined
): TimeTrackingEntryState {
  if (actualEnd == null) return TimeTrackingEntryState.RUNNING;
  if (endReason === TimeTrackingEndReason.SUSPENDED) {
    return TimeTrackingEntryState.SUSPENDED;
  }
  return TimeTrackingEntryState.FINISHED;
}

/**
 * Builds the full Tagesverlauf timeline for a day.
 * Pipeline: 1→raw projects, 2→pause projection, 2b→pause split, 3→add projects,
 * 4→add others, 5→compute gaps from raw, 6→sort, 7→(project merge removed),
 * 8→merge interruptions, 9→silent suspends, 10-11→Start/End of Day, 12→final sort.
 *
 * NOTE on Allgemein (general) gaps: an Allgemein entry is emitted for ANY real gap
 * between activities (no minimum-duration threshold). The `minGeneralGapDurationInMin`
 * setting is NOT consulted here — it governs only same-entry merging in the display
 * layer (`TimeTrackingHistory.vue`). Trailing gaps: open-ended (growing) only for an
 * open day that is today; bounded up to an explicit day-end when the day was closed;
 * none for a past day left open without a running entry.
 */
export function buildDisplayEntries(
  day: TimeTrackingDayEntry,
  t: (key: string) => string
): TimeTrackingDisplayEntry[] {
  const entries: TimeTrackingDisplayEntry[] = [];
  const { dayStart, dayEnd } = getDayBounds(day);
  const actualStart = getActualStartOfWork(day);
  const actualEnd = getActualEndOfWork(day);

  // ── 1. Build raw project blocks ──
  // Running entries (no workEndTime) stay genuinely open: they are NOT capped at
  // an unpaired S_SUSPEND. An open entry is displayed as "start –" with no duration
  // (an unpaired S_SUSPEND is still surfaced separately as a silent interruption in
  // step 6, but it never turns an open entry into a closed one).
  const rawProjects: RawProjectBlock[] = [];
  for (const pe of Object.values(day.projectEntries ?? {})) {
    const start = getProjectActualStart(pe);
    if (!start) continue;
    const end = getProjectActualEnd(pe);

    rawProjects.push({
      entryId: pe.id,
      projectId: pe.projectId,
      projectName: truncateProjectName(pe.projectName),
      phaseLabel:
        getAutoTimeTrackingContextLabel(pe.task) ??
        getPhaseLabel(pe.projectPhase),
      startTime: start,
      endTime: end,
      subEntries: buildSubEntries(pe, t),
      wasStartEdited: pe.userSetStartOfWork != null,
      wasEndEdited: pe.userSetEndOfWork != null,
      task: pe.task ?? null,
      countsAsTravel: pe.countsAsTravel === true,
      countsAsPause: pe.countsAsPause === true,
      suspendEntries: pe.suspendEntries,
      startSource: pe.startSource ?? null,
      endSource: pe.endSource ?? null,
      endReason: pe.endReason ?? null,
    });
  }
  rawProjects.sort((a, b) => a.startTime.getTime() - b.startTime.getTime());

  // ── 2. Project pauses from otherEntries into raw project blocks ──
  const assignedPauseIds = new Set<string>();
  const otherEntryValues = Object.values(day.otherEntries ?? {});
  for (const oe of otherEntryValues) {
    if (!oe.countsAsPause) continue;
    const pauseStart = getOtherActualStart(oe);
    if (!pauseStart) continue;
    const pauseEnd = getOtherActualEnd(oe);

    for (const proj of rawProjects) {
      // For running projects (endTime=null): any pause starting after project start
      // is eligible (matches IRES Work — no effective end needed).
      // For closed projects: pause must be fully within [start, end].
      let fullyContained: boolean;
      if (proj.endTime != null) {
        fullyContained = pauseEnd != null &&
          pauseStart.getTime() >= proj.startTime.getTime() &&
          pauseEnd.getTime() <= proj.endTime.getTime();
      } else {
        fullyContained = pauseStart.getTime() >= proj.startTime.getTime();
      }

      if (fullyContained) {
        proj.subEntries.push({
          id: `proj_pause_${oe.id}`,
          type: "pause",
          startTime: pauseStart,
          endTime: pauseEnd,
          label: t("timeTrackingHistory.categories.pause"),
          wasStartEdited: oe.userSetStartOfWork != null,
          wasEndEdited: oe.userSetEndOfWork != null,
        });
        proj.subEntries.sort((a, b) => (a.startTime?.getTime() ?? 0) - (b.startTime?.getTime() ?? 0));
        assignedPauseIds.add(oe.id);
        break;
      }
    }
  }

  // ── 2b. Split work sub-entries at pause boundaries ──
  // When a projected pause falls within a work sub-entry's time range, split the
  // work entry into before-pause and after-pause segments (matches IRES Work behavior).
  for (const proj of rawProjects) {
    const pauseSubs = proj.subEntries.filter(s =>
      s.type === "pause" && s.startTime != null && s.endTime != null
    );
    if (pauseSubs.length === 0) continue;

    const newSubs: TimelineSubEntry[] = [];
    for (const sub of proj.subEntries) {
      if (sub.type === "pause" || sub.startTime == null) {
        newSubs.push(sub);
        continue;
      }
      // For running sub-entries (endTime=null), use current time as effective end
      const subStart = sub.startTime as Date;
      const subEnd = (sub.endTime ?? new Date()) as Date;
      // Find pauses that fall strictly within this work/travel entry's range
      const overlapping = pauseSubs.filter(p => {
        const pStart = p.startTime as Date;
        return pStart.getTime() > subStart.getTime() &&
          pStart.getTime() < subEnd.getTime();
      }).sort((a, b) => (a.startTime as Date).getTime() - (b.startTime as Date).getTime());

      if (overlapping.length === 0) {
        newSubs.push(sub);
        continue;
      }

      // Split work entry at each pause boundary
      let segStart = sub.startTime;
      for (const pause of overlapping) {
        const pauseStart = pause.startTime as Date;
        // Work segment before this pause
        if (segStart.getTime() < pauseStart.getTime()) {
          newSubs.push({
            ...sub,
            id: `${sub.id}_pre_${pause.id}`,
            startTime: segStart,
            endTime: pause.startTime,
            wasEndEdited: false,
          });
        }
        segStart = pause.endTime as Date;
      }
      // Remaining work segment after last pause (preserve null endTime for running entries)
      if (sub.endTime == null || segStart.getTime() < sub.endTime.getTime()) {
        newSubs.push({
          ...sub,
          id: `${sub.id}_post`,
          startTime: segStart,
          endTime: sub.endTime,
          wasStartEdited: false,
        });
      }
    }
    proj.subEntries = newSubs.sort((a, b) =>
      (a.startTime?.getTime() ?? 0) - (b.startTime?.getTime() ?? 0)
    );
  }

  // ── 3. Add project blocks to timeline ──
  for (const proj of rawProjects) {
    entries.push({
      id: proj.entryId,
      type: "project",
      startTime: proj.startTime,
      endTime: proj.endTime,
      label: proj.projectName,
      color: TIMELINE_COLORS.green100,
      isRunning: proj.endTime === null,
      state: deriveEntryState(proj.endTime, proj.endReason),
      wasStartEdited: proj.wasStartEdited,
      wasEndEdited: proj.wasEndEdited,
      projectName: proj.projectName,
      projectId: proj.projectId,
      phaseLabel: proj.phaseLabel,
      subEntries: proj.subEntries.length > 0 ? proj.subEntries : undefined,
      task: proj.task,
      countsAsTravel: proj.countsAsTravel,
      countsAsPause: proj.countsAsPause,
      suspendEntries: proj.suspendEntries,
      startSource: proj.startSource,
      endSource: proj.endSource,
    });
  }

  // ── 4. Add other entries (pause, travel, interruption, other) ──
  for (const oe of Object.values(day.otherEntries ?? {})) {
    // Skip pauses already projected into projects
    if (assignedPauseIds.has(oe.id)) continue;

    const start = getOtherActualStart(oe);
    if (!start) continue;
    const end = getOtherActualEnd(oe);

    const wasStartEdited = oe.userSetStartOfWork != null;
    const wasEndEdited = oe.userSetEndOfWork != null;
    const startSource = oe.startSource ?? null;
    const endSource = oe.endSource ?? null;

    if (oe.countsAsInterruption) {
      entries.push({
        id: oe.id,
        type: "interruption",
        startTime: start,
        endTime: end,
        label: t("timeTrackingHistory.categories.interruption"),
        color: TIMELINE_COLORS.grey100,
        isRunning: end === null,
        wasStartEdited,
        wasEndEdited,
        startSource,
        endSource,
      });
    } else if (oe.countsAsPause) {
      entries.push({
        id: oe.id,
        type: "pause",
        startTime: start,
        endTime: end,
        label: t("timeTrackingHistory.categories.pause"),
        color: TIMELINE_COLORS.brown105,
        isRunning: end === null,
        wasStartEdited,
        wasEndEdited,
        startSource,
        endSource,
      });
    } else if (oe.countsAsTravel) {
      entries.push({
        id: oe.id,
        type: "travel",
        startTime: start,
        endTime: end,
        label: getTravelLabel(oe.location, false, t),
        color: TIMELINE_COLORS.baseYellow,
        isRunning: end === null,
        // A drive is the one `other` entry that says how it ended: UNTERBRECHEN leaves SUSPENDED,
        // ANGEKOMMEN leaves COMPLETED (IRES Work, from 2026-08-19). Without this the two look identical
        // here, which is what Hans reported.
        state: deriveEntryState(end, oe.endReason),
        wasStartEdited,
        wasEndEdited,
        startSource,
        endSource,
      });
    } else {
      entries.push({
        id: oe.id,
        type: "other",
        startTime: start,
        endTime: end,
        label: t("timeTrackingHistory.categories.internal"),
        color: TIMELINE_COLORS.baseYellow,
        isRunning: end === null,
        wasStartEdited,
        wasEndEdited,
        startSource,
        endSource,
      });
    }
  }

  // ── 5. Compute general gap entries from raw Firestore entries ──
  // Gaps computed from raw projectEntries + otherEntries. Completed entries use
  // their actual end; running entries use now as their effective end so they
  // advance lastEnd and prevent a trailing gap from appearing after them.
  // This happens BEFORE sorting and merging, matching IRES Work's
  // generalTimeHistoryItems computed property.
  if (actualStart) {
    const gapNow = new Date();
    const rawBoundaries: {
      start: Date;
      end: Date;
      startWasClamped: boolean;
      endWasClamped: boolean;
    }[] = [];
    for (const pe of Object.values(day.projectEntries ?? {})) {
      const s = getProjectActualStart(pe);
      const e = getProjectActualEnd(pe);
      if (!s) continue;
      const clamped = clampRangeToDay(s, e ?? gapNow, dayStart, dayEnd);
      if (clamped) rawBoundaries.push(clamped);
    }
    for (const oe of Object.values(day.otherEntries ?? {})) {
      const s = getOtherActualStart(oe);
      const e = getOtherActualEnd(oe);
      if (!s) continue;
      const clamped = clampRangeToDay(s, e ?? gapNow, dayStart, dayEnd);
      if (clamped) rawBoundaries.push(clamped);
    }
    rawBoundaries.sort((a, b) => a.start.getTime() - b.start.getTime());

    let lastEnd = new Date(Math.max(actualStart.getTime(), dayStart.getTime()));
    let lastEndWasClamped = actualStart.getTime() < dayStart.getTime();

    for (const boundary of rawBoundaries) {
      if (boundary.start.getTime() > lastEnd.getTime()) {
        // Any real gap surfaces an Allgemein entry. The gap threshold is NOT used
        // here — it governs only same-entry MERGING in the display layer. (Sub-minute
        // gaps collapse to 0m and are hidden by the table's zero-minute filter.)
        const gapMs = boundary.start.getTime() - lastEnd.getTime();
        if (gapMs > 0) {
          entries.push({
            id: `general_${lastEnd.getTime()}`,
            type: "general",
            startTime: lastEnd,
            endTime: boundary.start,
            label: t("timeTrackingHistory.categories.general"),
            color: TIMELINE_COLORS.blue100,
            isRunning: false,
            wasStartEdited: false,
            wasEndEdited: false,
            wasDayClamped: lastEndWasClamped || boundary.startWasClamped,
          });
        }
      }
      if (boundary.end.getTime() > lastEnd.getTime()) {
        lastEnd = boundary.end;
        lastEndWasClamped = boundary.endWasClamped;
      }
    }

    // Trailing gap logic.
    // - TODAY, day still open, nothing running: an open-ended Allgemein keeps growing
    //   from the last activity (shows current idle time).
    // - Day explicitly CLOSED (has a day-end): a bounded Allgemein fills any gap
    //   between the last activity and the day-end.
    // - A PAST day left open (no day-end) with the last activity already closed:
    //   NO trailing Allgemein — the user simply never closed the day, so there is no
    //   meaningful open gap to draw to. (Days with a running entry also get none; the
    //   running entry is the last row.)
    // The gap threshold is intentionally NOT applied to trailing gaps either.
    const hasRunningProject = Object.values(day.projectEntries ?? {}).some(pe =>
      getProjectActualStart(pe) != null && getProjectActualEnd(pe) == null
    );
    const hasRunningOther = Object.values(day.otherEntries ?? {}).some(oe =>
      getOtherActualStart(oe) != null && getOtherActualEnd(oe) == null
    );
    const hasRunningEntry = hasRunningProject || hasRunningOther;
    const isDayOpen = actualStart != null && actualEnd == null;
    const dayDate = day.date.toDate();
    const now = new Date();
    const isToday = dayDate.getFullYear() === now.getFullYear() &&
      dayDate.getMonth() === now.getMonth() &&
      dayDate.getDate() === now.getDate();
    const canBeOpenEnded = isToday && isDayOpen && !hasRunningEntry;

    if (canBeOpenEnded) {
      // Open-ended trailing gap (today only, unconditional, keeps growing).
      const dayEndForGap = new Date(Math.min(now.getTime(), dayEnd.getTime()));
      if (lastEnd.getTime() < dayEndForGap.getTime()) {
        entries.push({
          id: `general_trailing`,
          type: "general",
          startTime: lastEnd,
          endTime: null,
          label: t("timeTrackingHistory.categories.general"),
          color: TIMELINE_COLORS.blue100,
          isRunning: true,
          wasStartEdited: false,
          wasEndEdited: false,
          wasDayClamped: lastEndWasClamped,
        });
      }
    } else if (actualEnd != null) {
      // Day was explicitly ended: fill any positive gap up to the day-end.
      const dayEndForGap = new Date(Math.min(actualEnd.getTime(), dayEnd.getTime()));
      const dayEndWasClamped = dayEndForGap.getTime() !== actualEnd.getTime();
      if (dayEndForGap.getTime() > lastEnd.getTime()) {
        entries.push({
          id: `general_trailing`,
          type: "general",
          startTime: lastEnd,
          endTime: dayEndForGap,
          label: t("timeTrackingHistory.categories.general"),
          color: TIMELINE_COLORS.blue100,
          isRunning: false,
          wasStartEdited: false,
          wasEndEdited: false,
          wasDayClamped: lastEndWasClamped || dayEndWasClamped,
        });
      }
    }
    // else: past day left open with no running entry → no trailing Allgemein.
  }

  // ── 6. Merge silent suspend into its (open) parent entry ──
  // An unpaired trailing S_SUSPEND closes an entry that never got a workEndTime:
  //   - it is NOT followed by S_RESUME or P_SUSPEND in the same suspendEntries list, AND
  //   - the parent entry has no workEndTime (task was left running / not finished).
  // When multiple unpaired S_SUSPENDs exist in one list, the LATEST acts as the end.
  // The parent entry (and its trailing open sub-entry) is closed at the suspend time:
  // it then shows a duration, the suspend's source as end source, and "Stille
  // Unterbrechung" in the description (`silentlyInterrupted`). This replaces the older
  // behaviour of emitting a separate silent-interruption row. IRES Work hides these
  // entirely; IRES Office surfaces them (merged) for admin visibility.
  const suspendSources: { id: string; suspendEntries: SuspendEntry[]; hasEndTime: boolean }[] = [];
  for (const pe of Object.values(day.projectEntries ?? {})) {
    if (pe.suspendEntries?.length) {
      suspendSources.push({
        id: pe.id,
        suspendEntries: pe.suspendEntries,
        hasEndTime: getProjectActualEnd(pe) != null,
      });
    }
  }
  for (const oe of Object.values(day.otherEntries ?? {})) {
    if (oe.suspendEntries?.length) {
      suspendSources.push({
        id: oe.id,
        suspendEntries: oe.suspendEntries,
        hasEndTime: getOtherActualEnd(oe) != null,
      });
    }
  }
  for (const { id, suspendEntries: suspendList, hasEndTime } of suspendSources) {
    // If the parent entry finished normally, it was not silently interrupted.
    if (hasEndTime) continue;

    const sorted = [...suspendList].sort(
      (a, b) => a.startTime.toDate().getTime() - b.startTime.toDate().getTime()
    );

    // Collect all unpaired S_SUSPENDs (not followed by S_RESUME or P_SUSPEND).
    const unpairedSuspends: SuspendEntry[] = [];
    for (let i = 0; i < sorted.length; i++) {
      const se = sorted[i];
      if (se.suspendType !== SuspendType.S_SUSPEND) continue;
      let paired = false;
      for (let j = i + 1; j < sorted.length; j++) {
        if (sorted[j].suspendType === SuspendType.S_RESUME || sorted[j].suspendType === SuspendType.P_SUSPEND) {
          paired = true;
          break;
        }
        if (sorted[j].suspendType !== SuspendType.S_SUSPEND) break;
      }
      if (!paired) unpairedSuspends.push(se);
    }

    // Only the latest unpaired S_SUSPEND acts as the silent end.
    if (unpairedSuspends.length === 0) continue;
    const suspend = unpairedSuspends[unpairedSuspends.length - 1];
    const suspendTime = suspend.startTime.toDate();
    const suspendSource = suspend.operationSource ?? null;

    const parent = entries.find((e) => e.id === id);
    if (!parent) {
      // Defensive fallback: parent not in the display list (e.g. missing start) — surface
      // the suspend as a standalone row so it is never silently dropped.
      entries.push({
        id: `silent_suspend_${suspendTime.getTime()}`,
        type: "silentInterruption",
        startTime: suspendTime,
        endTime: null,
        label: t("timeTrackingHistory.categories.silentInterruption"),
        color: TIMELINE_COLORS.grey100,
        startSource: suspendSource,
        endSource: null,
        isRunning: false,
        wasStartEdited: false,
        wasEndEdited: false,
      });
      continue;
    }

    // Floor the silent end at the entry start to avoid negative durations on anomalous data.
    const parentStartMs = parent.startTime?.getTime() ?? suspendTime.getTime();
    const effectiveEnd =
      suspendTime.getTime() < parentStartMs ? new Date(parentStartMs) : suspendTime;

    parent.endTime = effectiveEnd;
    parent.isRunning = false;
    parent.endSource = suspendSource;
    parent.silentlyInterrupted = true;

    // Close the trailing open sub-entry at the same point so the displayed sub-row shows
    // the duration, the suspend's end source, and the "Stille Unterbrechung" description.
    if (parent.subEntries && parent.subEntries.length > 0) {
      for (let k = parent.subEntries.length - 1; k >= 0; k--) {
        const sub = parent.subEntries[k];
        if (sub.endTime == null) {
          sub.endTime = effectiveEnd;
          sub.endSource = suspendSource;
          sub.silentlyInterrupted = true;
          break;
        }
      }
    }
  }

  // ── 7. Sort all entries by start time ──
  entries.sort((a, b) => (a.startTime?.getTime() ?? 0) - (b.startTime?.getTime() ?? 0));

  // ── 8. (Project-entry merge removed by design) ──
  // Consecutive same-project entries are intentionally NOT merged into a single block.
  // Merging collapsed distinct entries (differing phase/source, and still-open entries)
  // into one row, which was misleading on this verification page: e.g. an entry showing a
  // single "Projekt fertig" phase for a span that actually contained separate "Leckortung"
  // entries and an open entry. Each project entry now renders as its own row. This
  // deliberately diverges from IRES Work's mergeAdjacentRuns.
  const MERGE_THRESHOLD_MS = 5 * 60 * 1000; // still used by interruption merge below
  const afterProjectMerge: TimeTrackingDisplayEntry[] = entries;

  // Project-entry overlap (`*`) detection is computed at display time by the component
  // via `computeOverlapMarkers()` (minute precision, per-boundary, Row-2 excludes
  // pause/travel). It is intentionally NOT stamped onto entries here.

  // ── 10. Merge consecutive interruptions within 5 min ──
  const afterInterruptionMerge: TimeTrackingDisplayEntry[] = [];
  for (const entry of afterProjectMerge) {
    const last = afterInterruptionMerge[afterInterruptionMerge.length - 1];
    if (
      entry.type === "interruption" &&
      last?.type === "interruption" &&
      last.endTime &&
      entry.startTime &&
      entry.startTime.getTime() - last.endTime.getTime() <= MERGE_THRESHOLD_MS
    ) {
      last.endTime = entry.endTime;
      last.wasEndEdited = entry.wasEndEdited;
    } else {
      afterInterruptionMerge.push(entry);
    }
  }

  // ── 11. Add Start of Day ──
  if (actualStart) {
    afterInterruptionMerge.push({
      id: "start_of_day",
      type: "startOfDay",
      startTime: actualStart,
      endTime: null,
      label: t("timeTrackingHistory.startOfDay"),
      color: "#FFFFFF",
      isRunning: false,
      wasStartEdited: day.userSetStartOfWork != null,
      wasEndEdited: false,
    });
  }

  // ── 12. Add End of Day ──
  if (actualEnd) {
    afterInterruptionMerge.push({
      id: "end_of_day",
      type: "endOfDay",
      startTime: actualEnd,
      endTime: null,
      label: t("timeTrackingHistory.endOfDay"),
      color: "#FFFFFF",
      isRunning: false,
      wasStartEdited: false,
      wasEndEdited: false,
    });
  }

  // ── 12. Final sort ──
  // startOfDay is always first, endOfDay is always last (regardless of timestamp)
  afterInterruptionMerge.sort((a, b) => {
    if (a.type === "startOfDay") return -1;
    if (b.type === "startOfDay") return 1;
    if (a.type === "endOfDay") return 1;
    if (b.type === "endOfDay") return -1;
    const aTime = a.startTime?.getTime() ?? 0;
    const bTime = b.startTime?.getTime() ?? 0;
    return aTime - bTime;
  });

  return afterInterruptionMerge;
}

/**
 * A minimal, framework-free description of a display item for same-entry merge
 * grouping. The concrete row/log shapes in the component map onto this so the
 * grouping logic stays pure and unit-testable.
 */
export interface MergeCandidate {
  /** Project id, or null for non-project rows (which never merge and break runs). */
  projectId: string | null;
  /** Phase/task identity: the task enum value, falling back to the phase label key. */
  phaseKey: string | null;
  /** Segment kind ("work" | "travel" | "pause" | null) so unlike segments never merge. */
  subType: string | null;
  /** Owning user id (Row 2 = startedByUserId; Row 1 is single-user so pass a constant). */
  userId: string | null;
  /** Start time in epoch ms. */
  startMs: number;
  /** End time in epoch ms, or null when the item is still open/running. */
  endMs: number | null;
}

/**
 * Groups **consecutive** display items into merge runs for the "same-entry merge"
 * UI feature (IRES Office Zeiterfassung verification page).
 *
 * Two neighbouring items join the same run only when ALL hold:
 * - both are project items with the **same** non-null `projectId`,
 * - same `phaseKey` (phase/task), same `subType`, same `userId`,
 * - **both are closed** (`endMs != null`) — a running item never merges,
 * - the empty gap between them is forward and within threshold:
 *   `0 <= next.startMs - prev.endMs <= gapThresholdMs` (compared against the
 *   immediately preceding run member's end).
 *
 * Adjacency is positional: callers MUST pass items in render/timeline order, so
 * any intervening item (pause, travel, other phase/user, a rendered gap row)
 * naturally ends the current run — e.g. `work → pause → work` yields three runs.
 * Grouping is transitive: a chain of matching neighbours collapses into one run.
 *
 * @returns one entry per run, each an array of the original indices (in order).
 *          Non-mergeable items appear as length-1 runs, so the union of all runs
 *          reproduces the input 1:1.
 */
export function groupMergeableRuns(
  items: MergeCandidate[],
  gapThresholdMs: number
): number[][] {
  const runs: number[][] = [];
  for (let i = 0; i < items.length; i++) {
    const curr = items[i];
    const run = runs[runs.length - 1];
    const prevIdx = run?.[run.length - 1];
    const prev = prevIdx != null ? items[prevIdx] : undefined;

    const canMerge =
      prev != null &&
      curr.projectId != null &&
      prev.projectId != null &&
      curr.projectId === prev.projectId &&
      curr.phaseKey === prev.phaseKey &&
      curr.subType === prev.subType &&
      curr.userId === prev.userId &&
      prev.endMs != null &&
      curr.endMs != null &&
      curr.startMs >= prev.endMs &&
      curr.startMs - prev.endMs <= gapThresholdMs;

    if (canMerge && run) {
      run.push(i);
    } else {
      runs.push([i]);
    }
  }
  return runs;
}

/**
 * Checks if a day has any entries (for highlight logic).
 */
export function dayHasEntries(day: TimeTrackingDayEntry): boolean {
  return (
    Object.keys(day.projectEntries ?? {}).length > 0 ||
    Object.keys(day.otherEntries ?? {}).length > 0 ||
    (day.additionalEntries ?? []).length > 0 ||
    Object.keys(day.autoTimeTrackingEntries ?? {}).length > 0
  );
}

/**
 * Calculates summary statistics for a day, matching IRES Project app formulas.
 */
export function buildDaySummary(day: TimeTrackingDayEntry, entries: TimeTrackingDisplayEntry[]): TimeTrackingDaySummary {
  // Total time window = endOfWork - startOfWork
  // For open (still-running) days, use current time as effective end.
  let totalMinutes = 0;
  const actualStart = getActualStartOfWork(day);
  const actualEnd = getActualEndOfWork(day);
  const effectiveEnd = actualEnd ?? (actualStart ? new Date() : null);
  if (actualStart && effectiveEnd) {
    totalMinutes = Math.round((effectiveEnd.getTime() - actualStart.getTime()) / (1000 * 60));
  }

  let travelMinutes = 0;
  let pauseMinutes = 0;
  let interruptionMinutes = 0;
  let otherMinutes = 0;
  let projectMinutes = 0;

  const now = new Date();
  for (const entry of entries) {
    const dur = calcDurationMinutes(entry.startTime, entry.endTime ?? now);
    switch (entry.type) {
      case "travel":
        travelMinutes += dur;
        break;
      case "pause":
        pauseMinutes += dur;
        break;
      case "other":
        otherMinutes += dur;
        break;
      case "interruption":
        interruptionMinutes += dur;
        break;
      case "project":
        projectMinutes += dur;
        break;
      // startOfDay, endOfDay, general, silentInterruption — not counted in summary totals
    }
  }

  // Also count pauses projected as sub-entries inside project blocks
  for (const entry of entries) {
    if (entry.type === "project" && entry.subEntries) {
      for (const sub of entry.subEntries) {
        if (sub.type === "pause") {
          const subDur = calcDurationMinutes(sub.startTime, sub.endTime ?? now);
          pauseMinutes += subDur;
        }
      }
    }
  }

  const workingMinutes = totalMinutes - pauseMinutes - interruptionMinutes;
  const trackedMinutes = projectMinutes + travelMinutes + otherMinutes + pauseMinutes + interruptionMinutes;
  const unexplainedMinutes = totalMinutes > 0 ? totalMinutes - trackedMinutes : 0;

  return {
    totalMinutes,
    travelMinutes,
    pauseMinutes,
    otherMinutes,
    projectMinutes,
    workingMinutes: Math.max(0, workingMinutes),
    unexplainedMinutes: Math.max(0, unexplainedMinutes),
    isClosed: day.closedBy != null,
  };
}

// ── Row 2: Project-level time tracking logs ──

/** Result of detecting active/last project from the user's day entries. */
export interface ActiveLastProjectResult {
  activeProjectId: string | null;
  activeProjectName: string | null;
  lastProjectId: string | null;
  lastProjectName: string | null;
}

/**
 * Detects the active and last-worked-on project across all days in a month document.
 *
 * - Active: first projectEntry (across any day) with no actual end and a start.
 * - Last: projectEntry with the latest createdBy.recordedAt (excluding the active project).
 */
export function detectActiveAndLastProject(
  month: TimeTrackingMonth | null
): ActiveLastProjectResult {
  const result: ActiveLastProjectResult = {
    activeProjectId: null,
    activeProjectName: null,
    lastProjectId: null,
    lastProjectName: null,
  };

  if (!month) return result;

  // Collect project entries from ALL days in the month
  const allEntries = Object.values(month.days ?? {}).flatMap((day) =>
    Object.values(day.projectEntries ?? {})
  );
  if (allEntries.length === 0) return result;

  // Active: no actual end time AND has an actual start time
  const active = allEntries.find(
    (e) => getProjectActualEnd(e) == null && getProjectActualStart(e) != null
  );
  if (active) {
    result.activeProjectId = active.projectId;
    result.activeProjectName = active.projectName;
  }

  // Last: most recent by createdBy.recordedAt (deduplicated by projectId)
  const withRecordedAt = allEntries.filter(
    (e) => e.createdBy?.recordedAt != null
  );
  if (withRecordedAt.length > 0) {
    // Keep only the latest entry per project
    const byProject = new Map<string, typeof withRecordedAt[0]>();
    for (const e of withRecordedAt) {
      const existing = byProject.get(e.projectId);
      if (!existing || e.createdBy.recordedAt.seconds > existing.createdBy.recordedAt.seconds) {
        byProject.set(e.projectId, e);
      }
    }
    const deduplicated = Array.from(byProject.values()).sort(
      (a, b) => b.createdBy.recordedAt.seconds - a.createdBy.recordedAt.seconds
    );

    // Pick last project (skip active if it's the most recent)
    for (const entry of deduplicated) {
      if (entry.projectId !== result.activeProjectId) {
        result.lastProjectId = entry.projectId;
        result.lastProjectName = entry.projectName;
        break;
      }
    }
  }

  return result;
}

/**
 * Detects active and last project from a single selected day using user-level
 * project entries (including travel and pause entries).
 *
 * Uses getProjectActualStart/End so entries without workStartTime but with
 * createdBy.recordedAt (e.g. older travel/pause project entries) are included.
 *
 * - Active: entry with an actual start and no actual end.
 * - Last: latest completed entry by actual end time (excluding the active project).
 */
export function detectActiveAndLastProjectFromDay(
  day: TimeTrackingDayEntry | null
): ActiveLastProjectResult {
  const result: ActiveLastProjectResult = {
    activeProjectId: null,
    activeProjectName: null,
    lastProjectId: null,
    lastProjectName: null,
  };

  if (!day) return result;

  const entries = Object.values(day.projectEntries ?? {}).filter(
    (entry) => getProjectActualStart(entry) != null
  );
  if (entries.length === 0) return result;

  const activeCandidates = entries
    .filter((entry) => getProjectActualEnd(entry) == null)
    .sort((a, b) => {
      const aStart = getProjectActualStart(a)?.getTime() ?? 0;
      const bStart = getProjectActualStart(b)?.getTime() ?? 0;
      return bStart - aStart;
    });

  const active = activeCandidates[0];
  if (active) {
    result.activeProjectId = active.projectId;
    result.activeProjectName = active.projectName;
  }

  const completedCandidates = entries
    .filter((entry) => getProjectActualEnd(entry) != null)
    .sort((a, b) => {
      const aEnd = getProjectActualEnd(a)?.getTime() ?? 0;
      const bEnd = getProjectActualEnd(b)?.getTime() ?? 0;
      if (bEnd !== aEnd) return bEnd - aEnd;
      const aStart = getProjectActualStart(a)?.getTime() ?? 0;
      const bStart = getProjectActualStart(b)?.getTime() ?? 0;
      return bStart - aStart;
    });

  const last = completedCandidates.find(
    (entry) => entry.projectId !== result.activeProjectId
  );
  if (last) {
    result.lastProjectId = last.projectId;
    result.lastProjectName = last.projectName;
  }

  return result;
}

/** Where a project's time tracking logs were read from. */
export enum ProjectTimeTrackingLogsSource {
  /** The live project document (`projects` / `leakDetectionProjects`). */
  LIVE = "LIVE",
  /** The project was archived; the logs come from its `archivedProjects` copy. */
  ARCHIVED = "ARCHIVED",
  /** The project was deleted (archived and marked deleted); logs come from the same copy. */
  DELETED = "DELETED",
}

/** A project's time tracking logs together with the state of the project they came from. */
export interface ProjectTimeTrackingLogsResult {
  logs: ProjectTimeTrackingLog[];
  source: ProjectTimeTrackingLogsSource;
}

/** Path of the single document holding a project's `autoTimeTrackingLogs` map. */
function getProjectLogsDocPath(projectId: string): string {
  return `${getProjectRef(projectId)}/logs/data`;
}

/** Sorts an `autoTimeTrackingLogs` map into a list, oldest entry first. */
function sortProjectTimeTrackingLogs(
  autoLogs: Record<string, ProjectTimeTrackingLog> | undefined | null
): ProjectTimeTrackingLog[] {
  return Object.values(autoLogs ?? {}).sort((a, b) => {
    const aTime = a.startedAtDate?.seconds ?? 0;
    const bTime = b.startedAtDate?.seconds ?? 0;
    return aTime - bTime; // oldest first
  });
}

/**
 * Fetches autoTimeTrackingLogs from a **live** project's logs/data document.
 * Returns entries sorted oldest-first by startedAtDate; empty for a project that is no
 * longer live (see `getArchivedProjectTimeTrackingLogs` for those).
 */
export async function getProjectTimeTrackingLogs(
  projectId: string
): Promise<ProjectTimeTrackingLog[]> {
  const doc = await db.doc(getProjectLogsDocPath(projectId)).get();

  if (!doc.exists) return [];

  return sortProjectTimeTrackingLogs(doc.data()?.autoTimeTrackingLogs);
}

/**
 * Fetches a project's time tracking logs from its `archivedProjects` copy — what is left of
 * an archived or deleted project. Both operations copy every project subcollection into
 * `archivedProjects/{projectId}` before deleting the originals, so `logs/data` (the
 * `autoTimeTrackingLogs` map) survives there unchanged.
 *
 * Returns a `LIVE` source with no logs when there is no archived copy: the project is either
 * still live (its logs are read from the live path) or was permanently deleted, which removes
 * the copy as well.
 */
async function getArchivedProjectTimeTrackingLogs(
  projectId: string
): Promise<ProjectTimeTrackingLogsResult> {
  const archivedProjectPath = getArchivedProjectRef(projectId);
  const [projectDoc, logsDoc] = await Promise.all([
    db.doc(archivedProjectPath).get(),
    db.doc(`${archivedProjectPath}/logs/data`).get(),
  ]);

  if (!projectDoc.exists) {
    return { logs: [], source: ProjectTimeTrackingLogsSource.LIVE };
  }

  // `deleted: true` is what both Cloud delete paths stamp on the archived document (alongside
  // `conclusion.conclusionState = DELETED`), and it predates the conclusion field, so it is the
  // one flag that also holds for older deletions.
  const isDeleted = projectDoc.data()?.deleted === true;

  return {
    logs: sortProjectTimeTrackingLogs(logsDoc.data()?.autoTimeTrackingLogs),
    source: isDeleted
      ? ProjectTimeTrackingLogsSource.DELETED
      : ProjectTimeTrackingLogsSource.ARCHIVED,
  };
}

/**
 * The tasks still running on a project, across all users (a project log is open while it has no
 * `endedAtDate`). Used to warn before an operation that forces those tasks to be suspended
 * server-side — deleting the project or moving it to the finished projects.
 */
export async function getOpenProjectTimeTrackingLogs(
  projectId: string
): Promise<ProjectTimeTrackingLog[]> {
  const logs = await getProjectTimeTrackingLogs(projectId);

  return logs.filter((log) => !log.endedAtDate);
}

/**
 * Subscribes to realtime updates on a project's autoTimeTrackingLogs, falling back to the
 * `archivedProjects` copy when the live logs document is gone — a user's day still shows the
 * entries of a project that has since been archived or deleted, so its project log must stay
 * readable too. The callback receives the source so the UI can mark those entries.
 *
 * Returns an unsubscribe function.
 */
export function onProjectTimeTrackingLogsSnapshot(
  projectId: string,
  onData: (result: ProjectTimeTrackingLogsResult) => void
): () => void {
  let cancelled = false;

  const unsubscribe = db
    .doc(getProjectLogsDocPath(projectId))
    .onSnapshot(async (doc) => {
      if (doc.exists) {
        onData({
          logs: sortProjectTimeTrackingLogs(doc.data()?.autoTimeTrackingLogs),
          source: ProjectTimeTrackingLogsSource.LIVE,
        });
        return;
      }

      // No live logs document: the project may have been archived or deleted, which moved its
      // whole `logs` subcollection into `archivedProjects`. That copy is never written again,
      // so a one-shot read is enough — no second listener.
      const archived = await getArchivedProjectTimeTrackingLogs(projectId);
      if (cancelled) return; // unsubscribed while the archive read was in flight
      onData(archived);
    });

  return () => {
    cancelled = true;
    unsubscribe();
  };
}

/**
 * Calls the previewOpenTimeTrackingAutoClose Cloud callable to detect whether
 * the authenticated user has a stale (unclosed, previous-day) time tracking entry.
 *
 * Returns the preview response including the proposal and feature-gate policy.
 * Errors must be caught by the caller — the callable may not exist yet on Cloud.
 */
export async function previewOpenTimeTrackingAutoClose(
  request?: PreviewOpenTimeTrackingAutoCloseRequest
): Promise<PreviewOpenTimeTrackingAutoCloseResponse> {
  const callable = functions.httpsCallable("previewOpenTimeTrackingAutoClose");
  const result = await callable(request ?? {});
  return result.data as PreviewOpenTimeTrackingAutoCloseResponse;
}

/**
 * Calls the applyOpenTimeTrackingAutoClose Cloud callable to close a stale
 * open time tracking entry. Must be preceded by a successful preview call.
 *
 * Returns the apply outcome including which entries were closed and whether a
 * project step was suspended. Errors must be caught by the caller.
 */
export async function applyOpenTimeTrackingAutoClose(
  request: ApplyOpenTimeTrackingAutoCloseRequest
): Promise<ApplyOpenTimeTrackingAutoCloseResponse> {
  const callable = functions.httpsCallable("applyOpenTimeTrackingAutoClose");
  const result = await callable(request);
  return result.data as ApplyOpenTimeTrackingAutoCloseResponse;
}
